/**
 * Pure math functions for skill mastery and success-chance computation.
 *
 * These functions are intentionally stateless — they take all necessary data
 * as arguments and return values without touching component or scene state.
 * Keeping them here makes them trivially testable in isolation and keeps
 * SkillProgressionComponent focused on XP bookkeeping and event dispatch.
 */

import { ConfigLoader } from '../systems/ConfigLoader';
import { DebugConfig } from '../debug/DebugConfig';
import { DebugLog } from '../debug/DebugLog';
import { clamp } from '../utils/Math';
import type { EffectivenessProfile, EffectivenessSignal, RecordUseOptions } from '../utils/Types';

/** Maps each signal name to its corresponding RecordUseOptions key. */
const EFFECTIVENESS_SIGNALS: ReadonlyArray<{ signal: EffectivenessSignal; key: keyof RecordUseOptions }> = [
  { signal: 'hitLanded',     key: 'hitLanded' },
  { signal: 'backstab',      key: 'backstab' },
  { signal: 'stealthAttack', key: 'stealthAttack' },
  { signal: 'multiHit',      key: 'multiHit' },
  { signal: 'killSecured',   key: 'killSecured' },
  { signal: 'enemyHasLOS',   key: 'enemyHasLOS' },
  { signal: 'dangerPresent', key: 'dangerPresent' },
];

/**
 * Compute the effectiveness multiplier from a node's profile and the use context.
 *
 * Returns a value in [1.0, profile.cap ?? defaultCap]. When no profile is
 * provided, or no matching signals fire, the multiplier is exactly 1.0 (no
 * bonus). Each signal that is both defined on the profile and set to true in
 * options contributes its bonus value to the total.
 */
export function computeEffectiveness(
  profile: EffectivenessProfile | undefined,
  options: RecordUseOptions | undefined,
  defaultCap: number,
): number {
  if (!profile || !options) return 1.0;
  let bonus = 0;
  for (const { signal, key } of EFFECTIVENESS_SIGNALS) {
    const signalBonus = profile.signals[signal];
    if (signalBonus !== undefined && options[key]) {
      bonus += signalBonus;
    }
  }
  return Math.min(1.0 + bonus, profile.cap ?? defaultCap);
}

/**
 * Compute the success chance for a skill given the tree's current XP.
 *
 * Passive and upgrade nodes always return 1.0 — only actives and evolutions
 * can fizzle. The chance climbs linearly from `baseSuccessChance` at 0 mastery
 * to 1.0 once the player has accumulated `masteryRange` XP beyond the node's
 * unlock threshold.
 *
 * @param skillId  - The skill node ID to evaluate.
 * @param treeXp   - Current XP accumulated in the tree that owns this skill.
 */
export function computeSkillSuccessChance(skillId: string, treeXp: number): number {
  if (DebugConfig.flags.alwaysSucceedSkills) return 1.0;

  const node = ConfigLoader.getV4Node(skillId);
  if (!node) return 1.0;

  // Only actives and evolutions can fizzle
  if (node.nodeType !== 'active' && node.nodeType !== 'evolution') return 1.0;

  const fizzle = ConfigLoader.xpGainConfig.fizzle;

  // Per-node override or global default
  const baseChance = ('baseSuccessChance' in node && typeof node.baseSuccessChance === 'number')
    ? node.baseSuccessChance
    : fizzle.defaultBaseSuccessChance;

  const mastery = treeXp - node.xpThreshold;
  const masteryRange = ConfigLoader.getMasteryXpRange(node.xpThreshold);

  return clamp(
    baseChance + (1.0 - baseChance) * (mastery / masteryRange),
    baseChance,
    1.0,
  );
}

/**
 * Roll a fizzle check for a skill. Returns `true` if the skill fires
 * successfully, `false` if it fizzles.
 *
 * Logs a debug line on failure so fizzle events remain visible in the
 * `skills` debug channel without polluting the call sites.
 *
 * @param skillId  - The skill node ID to evaluate.
 * @param treeXp   - Current XP accumulated in the tree that owns this skill.
 */
export function rollSkillSuccess(skillId: string, treeXp: number): boolean {
  const chance = computeSkillSuccessChance(skillId, treeXp);
  const roll = Math.random();
  const success = roll < chance;

  if (!success) {
    DebugLog.log('skills', `FIZZLE ${skillId}: roll ${roll.toFixed(2)} >= chance ${chance.toFixed(2)}`);
  }

  return success;
}
